/**
 * Display rules for an MLS listing's status.
 *
 * `mlsListings.isActive` is Homepoint's four-value bucket (ACTIVE, PENDING,
 * SOLD, EXPIRED) -- right for filtering, lossy for display. When the feed
 * supplies the MLS's own status string (`mlsStatus`), that string is what gets
 * shown, verbatim. East Tennessee REALTORS requires it: a listing in
 * "Pending - Continue to Show - Financing" may not be shown as just "Pending"
 * or "Under contract", and "Coming Soon - No Showings" may not be shown as an
 * ordinary active listing.
 */

/** The feed's status string, or null when it is absent or blank. */
export const cleanMlsStatus = (mlsStatus?: string | null): string | null => {
  const trimmed = mlsStatus?.trim();
  return trimmed ? trimmed : null;
};

/**
 * Whether the MLS's own status says anything beyond plain "Active". A listing
 * the feed calls just "Active" needs no status label; anything else it calls
 * an ACTIVE-bucket listing ("Coming Soon - No Showings") does.
 */
const isPlainActive = (mlsStatus: string): boolean =>
  mlsStatus.toLowerCase() === 'active';

/**
 * The status label to show for a listing, or null when it is plain active
 * inventory and needs none. The MLS's own string always wins over
 * `fallbackLabels`, which only cover feeds that supply no status string.
 */
export const listingStatusLabel = (
  bucket: string | null | undefined,
  mlsStatus: string | null | undefined,
  fallbackLabels: Record<string, string>
): string | null => {
  const raw = cleanMlsStatus(mlsStatus);
  if (raw) {
    return isPlainActive(raw) ? null : raw;
  }
  if (!bucket || bucket === 'ACTIVE') {
    return null;
  }
  return fallbackLabels[bucket] ?? bucket;
};
